/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *    Modified: Kirill Vizniuk - kirill@internalvalue.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { UserProvider } from 'src/app/providers';
import { Helpers, User } from 'src/app/model';
import { InputType } from 'src/app/model/enums/input.type';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { CrmAlertComponent, ConfirmationDialogComponent } from 'src/app/components/fields';
import { ChangePasswordModal, ChangeProfilePhotoModal, TfaModal } from 'src/app/components/modal';
import { AccountService, UserService } from '../../../../services';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { UserNamePref } from '../../../../model/user';
import Quill from 'quill';
import BlotFormatter from 'quill-blot-formatter';
Quill.register('modules/blotFormatter', BlotFormatter);
import * as _ from 'lodash';

@Component({
  selector: 'page-profile',
  templateUrl: 'profile.page.html',
  styleUrls: ['profile.page.scss']
})

export class ProfilePage implements OnInit, OnDestroy {
  @ViewChild('alert') alert: CrmAlertComponent;
  user: User;
  private userSub: any;
  private currentUserProfilePhotoSub: any;
  typeOfInput = InputType;
  isLoading = false;
  name: string;
  changeModal: BsModalRef;
  changeProfilePhotoModal: BsModalRef;
  modalRef: BsModalRef;
  saveSub: any;
  changeSub: any;

  htmlContent = '';
  avatarUrl: string;

  quillModules = {
    blotFormatter: {},
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image']
    ]
  };
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '15rem',
    minHeight: '5rem',
    placeholder: 'Enter text here...',
    translate: 'no'
  };

  constructor(
    private router: Router,
    private userProvider: UserProvider,
    public userService: UserService,
    private modalService: BsModalService,
    private accountService: AccountService
  ) {

    this.saveSub = this.accountService.onSave.subscribe(() => {
      this.onSave();
    });


    this.changeSub = this.accountService.onChangePassword.subscribe(() => {
      this.onChange();
    });
  }

  ngOnInit(): void {
    this.userSub = this.userService.user$.subscribe(u => {
      if (u){
        this.user = _.cloneDeep(u);
        if(this.user){
          this.avatarUrl = this.user.AVATAR_URL;
        }
      }   
    });
    this.currentUserProfilePhotoSub = this.userService.userProfilePhoto$.subscribe(u => {
      if (this.user && u !=undefined && u!=null){
        this.isLoading = true;
        this.user.AVATAR_URL = u;

        setTimeout(() => {
          this.isLoading = false;
        }, 1000);
      }
      
    });
  }

  ngOnDestroy(): void {
    if (this.userSub) this.userSub.unsubscribe();

    if (this.saveSub) this.saveSub.unsubscribe();

    if (this.changeSub) this.changeSub.unsubscribe();
    if (this.currentUserProfilePhotoSub) this.currentUserProfilePhotoSub.unsubscribe();
  }

  onSave(): void {
    this.alert.hide();
    this.userProvider.saveUser(this.user)
      .subscribe(
        data => {
          if (data.success) {
            this.alert.showSuccess('You successfully save the changes');
            this.userService.setUser(User.parse(data.result));
            setTimeout(() => {
              this.alert.hide();
            }, 3000);
          } else {
            this.alert.show(data.error);
          }
        });
  }

  onChange(): void {
    this.alert.hide();
    this.changeModal = this.modalService.show(ChangePasswordModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.changeModal.content.subject = ackSubject;
    this.changeModal.content.user = this.user;

    ackSubject.subscribe(value => {
      if (value) {
        this.alert.showSuccess('You successfully changed your passwod.');

        setTimeout(() => {
          this.alert.hide();
        }, 3000);
      }
    });
  }

  onSetTemplate(): void {
    this.htmlContent = '';
    this.htmlContent += `<div style="text-align:left;font-weight:bold;" dir="ltr">${this.user.getName()}</div>`;
    this.htmlContent += '<div style="text-align:left;margin-bottom:3px;" dir="ltr">Reliant Business Valuation</div>';
    this.htmlContent += '<img src="https://www.reliantvalue.com/wp-content/uploads/2017/07/Updated-small.jpg" width="130px"/><br/>';
    this.htmlContent += '<p style="text-align:left;font-family:Helvetica,Arial,sans-serif;font-size:12px;margin-bottom:0;margin-top:3px;color:#5F5F5F;line-height:135%;">';
    this.htmlContent += '<div>Your Most Reputable Source for SBA Values&reg; </div>';
    this.htmlContent += '<div>1246 S. River Road, Ste 101 | Cranbury, NJ 08512 </div>';
    this.htmlContent += '<div>Office: (908) 888-6030  | Fax: (908) 888-6040 </div>';
    this.htmlContent += '<div><a href="www.reliantvalue.com">www.reliantvalue.com</a></div></p>';
    this.htmlContent += '<br/>';

    this.user.SIGNATURE = this.htmlContent;
  }

  setPreference(value): void {
    switch (value) {
      case 'CORPORATE':
        this.user.NAME_PREF = UserNamePref.CORPORATE;
        break;
      case 'BUSINESS':
        this.user.NAME_PREF = UserNamePref.BUSINESS;
        break;
      case 'INTERNAL':
        this.user.NAME_PREF = UserNamePref.INTERNAL;
        break;
      default:
        this.user.NAME_PREF = UserNamePref.CORPORATE;
    }
  }

  onToggleChange(value: boolean): void {
    this.user.THEME_NIGHT = value;
  }

  setUpTfa(): void {
    if (this.user.TFA) {
      this.changeModal = this.modalService.show(ConfirmationDialogComponent, Helpers.getModalConfig());
      const ackSubject = new Subject<boolean>();
      this.changeModal.content.subject = ackSubject;
      this.changeModal.content.title = 'Set up Authenticator';
      this.changeModal.content.text = 'Are you sure you want to disable Two Factor Authentication?';
      this.changeModal.content.actionButton = 'Confirm';
      this.changeModal.content.actionButtonClass = '';

      ackSubject.subscribe(value => {
        if (value) {
          this.userProvider.disableTfa().subscribe(data => {
            if (data.success) {
              this.user.TFA = false;
            }
          });
        }
      });
    }
    else {
      this.changeModal = this.modalService.show(TfaModal, Helpers.getModalConfig());
      const ackSubject = new Subject<boolean>();
      this.changeModal.content.subject = ackSubject;
      this.changeModal.content.user = this.user;

      ackSubject.subscribe(value => {
        if (value) {
          this.user.TFA = true;
        }
      });
    }
  }

  onChangeProfilePhoto() {  
    this.alert.hide();
    this.changeProfilePhotoModal = this.modalService.show(ChangeProfilePhotoModal, Helpers.getModalConfig());
    const ackSubject = new Subject<string>();
    this.changeProfilePhotoModal.content.subject = ackSubject;
    ackSubject.subscribe(value => {
      if (value) {
        this.isLoading = true;
        this.userProvider.saveUserAvatar(value)
          .subscribe(
            data => {
              this.isLoading = false;
              if (data.success) {
                this.userService.setUserProfilePhoto(data.result.NAME);
                this.alert.showSuccess('You successfully changed your profile photo.');
                setTimeout(() => {
                  this.alert.hide();
                }, 3000);
              } else {
                this.alert.show(data.error);
              }
            }, () => {
              this.isLoading = false;
             });
      }
    });
  }

  onDeleteProfilePhoto() {  
    this.alert.hide();
    this.isLoading = true;
        this.userProvider.deleteUserAvatar()
          .subscribe(
            data => {
              this.isLoading = false;
              if (data.success) {
                this.userService.setUserProfilePhoto('');
                this.alert.showSuccess('You successfully remove your profile photo.');
                setTimeout(() => {
                  this.alert.hide();
                }, 3000);
              } else {
                this.alert.show(data.error);
              }
            }, () => {
              this.isLoading = false;
             });
  }
}
